/**
 * RowHeaderSlot - Renders controls in the row-header location.
 *
 * This slot appears before each row (to the left) with vertically oriented text.
 */

import React from 'react';

import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from '../../Component';
import {type StepMeta} from '../../types';
import {LAYOUT_CONSTANTS} from '../constants';
import {ControlRenderer} from '../renderers/ControlRenderer';
import {type ControlDefinition, type GridRow} from '../types';

export type RowHeaderSlotProps = {
  controls: ControlDefinition[];
  row: GridRow;
  rowIndex?: number;
  disableRunLinks?: boolean;
  config: MediaConfigWithDefaults;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  stepMeta: StepMeta;
  mediaSliderKey: string;
};

/**
 * Renders controls in the row-header location.
 *
 * All controls are rendered through ControlRenderer, which delegates to the
 * appropriate control component that handles its own rendering.
 * Wrapped with vertical rotation styling for row headers.
 *
 * @param props - Row header slot props
 * @returns Rendered row header or null if hidden/empty
 */
export const RowHeaderSlot: React.FC<RowHeaderSlotProps> = ({
  controls,
  row,
  rowIndex,
  disableRunLinks,
  config,
  updateConfig,
  stepMeta,
}) => {
  // Don't render if row is empty
  if (row.cells.length === 0 || row.cells[0].isEmpty) {
    return null;
  }

  return (
    <div
      className="flex flex-1 items-center justify-center text-sm h-full"
      style={{
        width: LAYOUT_CONSTANTS.ROW_HEADER_WIDTH,
      }}>
      <div className="flex flex-1 items-center justify-center overflow-hidden text-ellipsis whitespace-nowrap px-4 py-8">
        {controls.map((control, idx) => (
          <ControlRenderer
            // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
            key={`row-header-control-${idx}`}
            control={control}
            index={idx}
            config={config}
            updateConfig={updateConfig}
            stepMeta={stepMeta}
            rowIndex={rowIndex}
            disableRunLinks={disableRunLinks}
          />
        ))}
      </div>
    </div>
  );
};
